import { LitElement, html, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { IGif } from '@giphy/js-types';

import { getGifById } from '@amityeko/pbc-core/dist/utilities/giphy';

import '@amityeko/design-system/dist/elements/typography/tg-title';
import '@amityeko/design-system/dist/elements/typography/tg-caption';
import '@amityeko/design-system/dist/elements/typography/tg-label-one';
import '@amityeko/design-system/dist/elements/chip';
import '@amityeko/design-system/dist/elements/video';

import '../image-layout-content';
import { Images } from '../image-layout/types';
import { LayoutList } from '../image-layout-list/types';

import styles from './styles';

@customElement('rnr-recognition-preview')
export default class RnRRecognitionPreview extends LitElement {
  static styles = styles;

  @property({ type: Number })
  scale?: number;

  @property()
  badgeSrc: string = '';

  @property()
  badgeName?: string;

  @property({ type: Number })
  badgePoint?: number;

  @property()
  bgFromColor?: string;

  @property()
  bgToColor?: string;

  @property()
  bgImage?: string;

  @property()
  animation?: string;

  @property()
  textColor?: string;

  @property()
  text?: string;

  @property()
  gifId?: string;

  @property({ type: Object })
  gif?: IGif;

  @property({ type: Object })
  images?: Images;

  @property()
  videoURL?: string;

  @property()
  videoTitle?: string;

  @property()
  videoTitleColor?: string;

  @property({ type: Boolean })
  isTitleEmbedded: boolean = false;

  @property()
  audioURL?: string;

  @property()
  audioTitle?: string;

  @property()
  audioTitleColor?: string;

  @state()
  gifUrl?: string;

  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('gifId') && this.gifId) {
      getGifById(this.gifId).then((data: any) => {
        this.gifUrl = data?.images?.preview_gif?.url;
      });
    } else {
      if (!this.gifId) {
        this.gifUrl = undefined;
      }
    }
  }

  get normilizedScale(): string {
    if (!this.scale) return '1';
    if (this.scale < 0.75) return '0.75';
    if (this.scale > 2) return '2';

    return String(this.scale);
  }

  private _getNormalizedGifUrl(): string | undefined {
    if (this.gif?.images?.preview_gif?.url) {
      return this.gif?.images?.preview_gif?.url;
    }

    if (this.gifUrl) {
      return this.gifUrl;
    }

    return undefined;
  }

  private _renderTextAndAudio() {
    const finalGifUrl = this._getNormalizedGifUrl();

    return html`
      <div class="preview" style=${styleMap({ '--private-scale': this.normilizedScale })}>
        <amityeko-chip class="preview__chip" selectionColor="#424242">
          <img slot="prefix" class="preview__chip__image" src=${this.badgeSrc} />
          <amityeko-tg-caption>${this.badgeName} ${this.badgePoint} points</amityeko-tg-caption>
        </amityeko-chip>

        <div
          class="preview__backgroundGradient"
          style=${styleMap({
            '--private-bg-color-one': this.bgFromColor,
            '--private-bg-color-two': this.bgToColor,
          })}
        ></div>
        ${this.bgImage ? html`<img class="preview__backgroundImage" src=${this.bgImage} />` : null}
        ${this.animation ? html`<img class="preview__animation" src=${this.animation} />` : null}

        <div class="preview__content">
          <amityeko-tg-label-one
            style=${styleMap({
              '--private-text-color': this.textColor || this.audioTitleColor,
            })}
          >
            ${this.text || this.audioTitle}
          </amityeko-tg-label-one>

          ${this.audioURL
            ? html`
                <rnr-audio
                  .audioURL=${this.audioURL}
                  ?shouldShowRemainingTime=${true}
                  .theme=${'light'}
                ></rnr-audio>
              `
            : null}
          ${finalGifUrl ? html`<img class="preview__content__gif" src=${finalGifUrl} />` : null}

          <rnr-image-layout-content
            style=${styleMap({
              '--private-image-layout-display': this.images ? 'block' : 'none',
            })}
            .imageCount=${this.images?.imageCount || 1}
            .currentLayout=${this.images?.layout || LayoutList.SQUARE}
            ?isViewOnly=${true}
            .imageKeys=${this.images?.imageKeys || []}
            .width=${'140px'}
            .height=${'140px'}
          ></rnr-image-layout-content>
        </div>
      </div>
    `;
  }

  private _renderVideo() {
    return html`<div
      class="preview preview--video"
      style=${styleMap({ '--private-scale': this.normilizedScale })}
    >
      <rnr-video
        .videoURL=${this.videoURL}
        .videoTitle=${this.videoTitle}
        .videoTitleColor=${this.videoTitleColor}
        .animation=${this.animation}
        ?isTitleEmbedded=${this.isTitleEmbedded}
        ?isShowControls=${true}
      >
      </rnr-video>
      <amityeko-chip class="preview__chip" selectionColor="#424242">
        <img slot="prefix" class="preview__chip__image" src=${this.badgeSrc} />
        <amityeko-tg-caption>${this.badgeName} ${this.badgePoint} points</amityeko-tg-caption>
      </amityeko-chip>
    </div>`;
  }

  protected render() {
    return html`${this.videoURL ? this._renderVideo() : this._renderTextAndAudio()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-recognition-preview': RnRRecognitionPreview;
  }
}
